import { useState, useEffect, useContext, useMemo } from 'react';
import { Chip, Grid } from '@mui/material';
import {
    Done,
    Error as ErrorIcon,
    HourglassEmpty,
    Refresh,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SttDialog } from '../../../sporttia/dialogs/SttDialog';
import { AppContext } from '../../../sporttia/AppContext';
import {
    SttChipUser,
    SttFormTime,
    SttSelectWeekdays,
    SttTable,
} from '../../../sporttia/all';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import DateBuilder from '../../../lib/DateBuilder';
import { useLoader } from '../../../lib/hooks';
import { parseToDate } from '../../../lib/utils';
import { isSlotTimeRangeValid } from './timeRange';
import { PERMISSIONS } from '../../../lib/permissions';

function areWeekdaysEqual(
    a: number[] | undefined,
    b: number[] | undefined,
): boolean {
    const arrA = a || [];
    const arrB = b || [];
    if (arrA.length !== arrB.length) return false;
    const sortedA = [...arrA].sort((x, y) => x - y);
    const sortedB = [...arrB].sort((x, y) => x - y);
    return sortedA.every((val, idx) => val === sortedB[idx]);
}

// Normalize time to HH:mm format for comparison
function normalizeTime(time: string | undefined): string {
    if (!time) return '';
    return time.split(':').slice(0, 2).join(':');
}

type Slot = {
    id?: number;
    ini: string;
    end: string;
    weekdays: number[];
};

type Period = {
    id: number;
    creator?: {
        fullName: string;
    };
    facility?: {
        id: number;
        name: string;
    };
    ini: string;
    end: string;
    status: string;
    error?: string;
};

type PeriodsResponse = {
    rows: Period[];
    count: number;
    page: number;
    pages: number;
};

type FilterParams = {
    ini?: string | null;
    end?: string | null;
    status?: string | string[];
    page?: number;
    rows?: number;
};

type Props = {
    item: Slot | null;
    onSave: (slot: Slot) => void;
    onDelete: (slot: Slot) => void;
    onClose: () => void;
    loading?: boolean;
    parentIni?: string;
    parentEnd?: string;
};

/**
 * SlotDialog - dialog para la visualización de Tramos (slots)
 * <Slot>Object item - el objeto de tipo Slot
 * Function onSave - callback para el botón de guardado
 * Function onDelete - callback para el botón de borrado
 * Function onClose - callback para el botón cerrar
 * */
export function BusySlotDialog({
    item,
    onSave,
    onDelete,
    onClose,
    loading,
    parentIni,
    parentEnd,
}: Props): React.ReactElement | null {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;

    const [slot, setSlot] = useState<Slot | null>(item);
    const [originalSlot, setOriginalSlot] = useState<Slot | null>(item);
    const [periods, setPeriods] = useState<PeriodsResponse>();

    const [preset, setPreset] = useState<string>('ALL');
    const [filterParams, setFilterParams] = useState<FilterParams>({});
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [rowsPerPage] = useState<number>(5);
    const [periodLoadStatus, setPeriodLoadStatus] = useState<string>('idle');
    // Cargar periodos para este slot
    function loadPeriods(id: number, filtParams: FilterParams): void {
        setPeriodLoadStatus('loading');
        cxt?.api('GET', `/busies/schedules/slots/${id}/periods`, {
            params: filtParams,
            success: (response: PeriodsResponse) => {
                if (response.rows) {
                    setPeriods(response);
                    setPeriodLoadStatus('idle');
                }
            },
            error: (response: unknown) => {
                const errorResponse = response as { error?: { msg: string } };
                cxt?.showMessage(
                    'E',
                    errorResponse?.error?.msg || 'Unknown error',
                );
                setPeriodLoadStatus('idle');
            },
        });
    }
    // TODO: Mismo comportamiento que en reservas fijas, habría que crear un componente especifico para manejar tareas
    // asincronas
    const BUSY_STATUS = {
        PENDING: 'PENDING',
        PUBLISHED: 'PUBLISHED',
        PROCESSED: 'PROCESSED',
        FAILED: 'FAILED',
    };
    async function doFiltering(
        params: { ini?: string; end?: string; status?: string } = {},
    ): Promise<void> {
        const { ini = null, end = null, status } = params;

        // Actualizamos el preset solo si se proporciona un status en los parámetros
        if (status !== undefined) {
            setPreset(status);
        }

        const baseFilters: FilterParams = {
            ...(ini !== undefined && { ini }),
            ...(end !== undefined && { end }),
            page: 1,
            rows: rowsPerPage,
        };

        const statusToUse = status !== undefined ? status : preset;

        if (statusToUse === 'IN_PROGRESS') {
            setFilterParams({
                ...baseFilters,
                status: 'IN_PROGRESS',
            });
            loadPeriods(item?.id!, {
                ...baseFilters,
                status: [BUSY_STATUS.PENDING, BUSY_STATUS.PUBLISHED],
            });
        } else if (statusToUse && statusToUse !== 'ALL') {
            setFilterParams({ ...baseFilters, status: statusToUse });
            loadPeriods(item?.id!, { ...baseFilters, status: statusToUse });
        } else {
            setFilterParams({
                ...baseFilters,
                status: 'ALL',
            });
            loadPeriods(item?.id!, {
                ...baseFilters,
                status: Object.values(BUSY_STATUS),
            });
        }
    }
    // Deep copy helper to prevent mutation issues with weekdays array
    const cloneSlot = (slot: Slot | null): Slot | null =>
        slot
            ? { ...slot, weekdays: slot.weekdays ? [...slot.weekdays] : [] }
            : null;

    // item cambia. Si el contiene id, cargar los periodos asociados.
    useEffect(() => {
        setSlot(cloneSlot(item));
        setOriginalSlot(cloneSlot(item));
        if (item && item.id) {
            doFiltering();
        } else {
            setPeriods(undefined);
        }
    }, [item]);

    // Check if slot has been modified (only for existing slots with id)
    const hasChanges = useMemo(() => {
        if (!slot || !originalSlot || !slot.id) return true; // Allow save for new slots
        return (
            normalizeTime(slot.ini) !== normalizeTime(originalSlot.ini) ||
            normalizeTime(slot.end) !== normalizeTime(originalSlot.end) ||
            !areWeekdaysEqual(slot.weekdays, originalSlot.weekdays)
        );
    }, [slot, originalSlot]);

    // cambios en los campos
    function handleChange({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void {
        if (!name) return;
        setSlot({ ...slot!, [name]: value });
    }

    // Helper: filtros comunes
    const getCleanFilters = (
        customParams: Record<string, unknown> = {},
    ): Record<string, unknown> => {
        const clean: Record<string, unknown> = { ...customParams };
        if (filterParams?.ini) {
            clean.ini = new DateBuilder(filterParams.ini).ymd();
        }
        if (filterParams?.end) {
            clean.end = new DateBuilder(filterParams.end).ymd();
        }
        if (filterParams?.status !== null) clean.status = filterParams.status;
        return clean;
    };

    const STATUS_STYLES = {
        IN_PROGRESS: {
            label: 'EN PROCESO',
            color: '#2196f3',
            icon: HourglassEmpty,
            includes: [BUSY_STATUS.PENDING, BUSY_STATUS.PUBLISHED],
        },
        SUCCEEDED: {
            label: 'FINALIZADO',
            color: '#4caf50',
            icon: Done,
            includes: [BUSY_STATUS.PROCESSED],
        },
        ERROR: {
            label: 'ERROR',
            color: '#f44336',
            icon: ErrorIcon,
            includes: [BUSY_STATUS.FAILED],
        },
    } as const;

    const getStatusConfig = (status: string) => {
        const statusConfig = Object.values(STATUS_STYLES).find((config) =>
            config.includes?.includes(status),
        );

        if (!statusConfig) {
            const IconComponent = STATUS_STYLES.IN_PROGRESS
                .icon as React.ComponentType<{ style: React.CSSProperties }>;
            return {
                label: STATUS_STYLES.IN_PROGRESS.label,
                color: STATUS_STYLES.IN_PROGRESS.color,
                includes: STATUS_STYLES.IN_PROGRESS.includes,
                icon: (
                    <IconComponent style={{ color: 'white', fontSize: 16 }} />
                ),
            };
        }

        const IconComponent = statusConfig.icon as React.ComponentType<{
            style: React.CSSProperties;
        }>;
        return {
            label: statusConfig.label,
            color: statusConfig.color,
            includes: statusConfig.includes,
            icon: <IconComponent style={{ color: 'white', fontSize: 16 }} />,
        };
    };

    const renderStatus = (status: string): React.ReactElement => {
        const config = getStatusConfig(status);

        return (
            <Chip
                icon={config.icon}
                size="small"
                label={config.label}
                style={{
                    backgroundColor: config.color,
                    color: 'white',
                    fontWeight: 500,
                }}
            />
        );
    };

    const handleRefresh = async (): Promise<void> => {
        const filters: FilterParams = {
            ...getCleanFilters(),
            page: currentPage,
            rows: rowsPerPage,
        };

        if (preset === 'IN_PROGRESS') {
            loadPeriods(item?.id!, {
                ...filters,
                status: [BUSY_STATUS.PENDING, BUSY_STATUS.PUBLISHED],
            });
        } else if (preset !== 'ALL') {
            loadPeriods(item?.id!, {
                ...filters,
                status: preset,
            });
        } else {
            loadPeriods(item?.id!, {
                ...filters,
                status: Object.values(BUSY_STATUS),
            });
        }
    };
    const [, loader] = useLoader([periodLoadStatus]);

    // Validates the slot and returns it with invalid weekdays filtered out,
    // or null if the slot is not valid.
    const getValidatedSlot = (): Slot | null => {
        // Check if at least one weekday is selected
        if (!slot?.weekdays || slot.weekdays.length === 0) {
            cxt?.showMessage('E', t('validation.atLeastOneWeekday'));
            return null;
        }

        let validWeekdays = [...slot.weekdays];

        // Filter out weekdays that fall outside the parent schedule's date range
        if (parentIni && parentEnd) {
            const parentStart = parseToDate(parentIni);
            const parentEndDate = parseToDate(parentEnd);

            if (parentStart && parentEndDate) {
                const current = new Date(parentStart);
                current.setHours(0, 0, 0, 0);

                const endDt = new Date(parentEndDate);
                endDt.setHours(0, 0, 0, 0);

                const diffTime = Math.abs(endDt.getTime() - current.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays < 6) {
                    const availableWeekdays = new Set<number>();
                    const iter = new Date(current);
                    while (iter.getTime() <= endDt.getTime()) {
                        availableWeekdays.add(iter.getDay());
                        iter.setDate(iter.getDate() + 1);
                    }

                    validWeekdays = slot.weekdays.filter((day) =>
                        availableWeekdays.has(day),
                    );

                    if (validWeekdays.length === 0) {
                        cxt?.showMessage(
                            'E',
                            t('validation.weekdaysNotInRange'),
                        );
                        return null;
                    }
                }
            }
        }

        // Check if "from" time is before "until" time.
        // A "00:00" end means midnight/end-of-day (24:00), handled by isSlotTimeRangeValid.
        if (slot?.ini && slot?.end) {
            if (!isSlotTimeRangeValid(slot.ini, slot.end)) {
                cxt?.showMessage('E', t('validation.fromBeforeUntil'));
                return null;
            }
        }

        return { ...slot!, weekdays: validWeekdays };
    };

    const handleSave = (): void => {
        const validatedSlot = getValidatedSlot();
        if (validatedSlot) {
            onSave(validatedSlot);
        }
    };

    if (!slot) {
        return null;
    }

    return (
        <SttDialog
            open={slot !== null}
            maxWidth="lg"
            onClose={onClose}
            title={slot.id ? t('Slot') : t('CreateTimeSlot')}
            content={
                <>
                    {loader}
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <SttSelectWeekdays
                                selected={slot.weekdays}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            {/* El output de esto es tipo HH:mm:ss así que lo convertimos a HH:mm para el Slot */}
                            <SttFormTime
                                name="ini"
                                caption={t('From')}
                                defVal={slot.ini}
                                min="00:00:00"
                                max="23:45:00"
                                interval={5}
                                onChange={({
                                    name,
                                    value,
                                }: {
                                    name: string | undefined;
                                    value: unknown;
                                }) =>
                                    handleChange({
                                        name,
                                        value: (value as string)
                                            .split(':')
                                            .splice(0, 2)
                                            .join(':'),
                                    })
                                }
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            {/* El output de esto es tipo HH:mm:ss así que lo convertimos a HH:mm para el Slot */}
                            <SttFormTime
                                name="end"
                                caption={t('Until')}
                                defVal={slot.end}
                                min="00:00:00"
                                max="23:45:00"
                                interval={5}
                                onChange={({
                                    name,
                                    value,
                                }: {
                                    name: string | undefined;
                                    value: unknown;
                                }) =>
                                    handleChange({
                                        name,
                                        value: (value as string)
                                            .split(':')
                                            .splice(0, 2)
                                            .join(':'),
                                    })
                                }
                            />
                        </Grid>
                        {periods && (
                            <Grid size={{ xs: 12 }}>
                                <SttTopFilteringControls
                                    extraFields={[
                                        {
                                            caption: t('Status'),
                                            type: 'select',
                                            name: 'status',
                                            value: preset,
                                            options: [
                                                {
                                                    caption: t('All'),
                                                    value: 'ALL',
                                                },
                                                {
                                                    caption: t(
                                                        'booking.status.in_progress',
                                                    ),
                                                    value: 'IN_PROGRESS',
                                                },
                                                {
                                                    caption: t(
                                                        'booking.status.succeeded',
                                                    ),
                                                    value: 'PROCESSED',
                                                },
                                                {
                                                    caption: t('Error'),
                                                    value: 'FAILED',
                                                },
                                            ],
                                        },
                                        {
                                            caption: t('Begin'),
                                            name: 'ini',
                                            type: 'date',
                                            value: '',
                                        },
                                        {
                                            caption: t('End'),
                                            name: 'end',
                                            type: 'date',
                                            value: '',
                                        },
                                    ]}
                                    skipFirstFiltering
                                    onFilter={async (
                                        params: Record<string, unknown>,
                                    ) => {
                                        // Pasamos el estado actual del select en los parámetros
                                        const filterData: {
                                            ini?: string;
                                            end?: string;
                                            status?: string;
                                        } = {};

                                        if (params?.ini) {
                                            filterData.ini = new DateBuilder(
                                                params.ini as string,
                                            ).ymd();
                                        }

                                        if (params?.end) {
                                            filterData.end = new DateBuilder(
                                                params.end as string,
                                            ).ymd();
                                        }

                                        filterData.status =
                                            (params.status as string) || preset;

                                        doFiltering(filterData);
                                    }}
                                    iconButtons={[
                                        {
                                            icon: <Refresh />,
                                            onClick: handleRefresh,
                                            caption: t('Refresh'),
                                        },
                                    ]}
                                />
                                <SttTable
                                    autoload={false}
                                    perPage={rowsPerPage}
                                    onFetch={(params: FilterParams) => {
                                        const cleanFilters = getCleanFilters();
                                        const newPage = params.page || 1;
                                        setCurrentPage(newPage);

                                        // Combinamos los filtros activos con los parámetros de paginación
                                        const fetchParams: FilterParams = {
                                            ...cleanFilters, // Filtros activos
                                            ...params, // Parámetros de paginación
                                            page: newPage,
                                            rows: params.rows || rowsPerPage,
                                        };

                                        // mantener status si está definido
                                        if (preset === 'IN_PROGRESS') {
                                            fetchParams.status = [
                                                BUSY_STATUS.PENDING,
                                                BUSY_STATUS.PUBLISHED,
                                            ];
                                        } else if (
                                            preset &&
                                            preset !== 'ALL' &&
                                            !params.status
                                        ) {
                                            fetchParams.status = preset;
                                        } else if (preset === 'ALL') {
                                            fetchParams.status =
                                                Object.values(BUSY_STATUS);
                                        }

                                        loadPeriods(item?.id!, fetchParams);
                                    }}
                                    data={periods}
                                    onClickRow={(row: Period) => {
                                        if (row?.error) {
                                            const msg = row.error;
                                            cxt?.showMessage('E', msg);
                                        }
                                    }}
                                    columns={[
                                        {
                                            align: 'left',
                                            title: t('User'),
                                            value: (row: Period) => (
                                                <SttChipUser
                                                    user={{
                                                        login:
                                                            row.creator
                                                                ?.fullName ||
                                                            '',
                                                    }}
                                                />
                                            ),
                                        },
                                        {
                                            title: t('Date'),
                                            type: 'dmy',
                                            value: (row: Period) => row.ini,
                                        },
                                        {
                                            title: t('Time'),
                                            type: 'hmRange',
                                            value: (row: Period) => ({
                                                ini: row.ini,
                                                end: row.end,
                                            }),
                                        },
                                        {
                                            align: 'left',
                                            title: t('Facility'),
                                            value: (row: Period) =>
                                                row.facility?.name ?? '',
                                        },
                                        {
                                            title: t('Status'),
                                            field: 'status',
                                            value: (row: Period) =>
                                                renderStatus(row.status),
                                        },
                                    ]}
                                />
                            </Grid>
                        )}
                    </Grid>
                </>
            }
            buttons={[
                {
                    type: 'save',
                    onClick: handleSave,
                    permission: PERMISSIONS.CLOSURE.MODIFY,
                    disabled: loading || !hasChanges,
                },
                slot.id
                    ? {
                          type: 'delete',
                          onClick: () => onDelete(slot),
                          permission: PERMISSIONS.CLOSURE.MODIFY,
                      }
                    : null,
                {
                    type: 'close',
                    onClick: onClose,
                },
            ]}
        />
    );
}
